/**
 * @fileoverview Welcome screen component for interactive team chat.
 * Renders instead of the standard empty state when interactiveChat is enabled.
 */

import { AgentIcon } from "@/components/agents/agent-icon";
import { Button } from "@/components/ui/button";
import { Sparkles } from "lucide-react";

interface InteractiveWelcomeScreenProps {
  /** Team display name */
  teamName: string;
  /** Team description text (kept for API compat, not rendered) */
  teamDescription?: string;
  /** Team icon emoji or URL */
  teamIcon?: string;
  /** Called when user clicks "Get Started" */
  onGetStarted: () => void;
}

/**
 * Welcome screen for interactive team chat flow.
 * Shows team avatar, welcome message, and a "Get Started" button
 * that transitions to the form step (or directly to chat).
 */
export function InteractiveWelcomeScreen({
  teamName,
  teamIcon,
  onGetStarted,
}: InteractiveWelcomeScreenProps) {
  return (
    <div className="flex size-full flex-col items-center justify-center bg-background">
      <div className="mx-auto w-full max-w-lg px-4 text-center">
        {/* Team avatar with decorative ring */}
        <div className="mb-6 flex items-center justify-center">
          <div className="rounded-full bg-primary/5 p-4">
            <AgentIcon
              avatar={teamIcon}
              name={teamName}
              size="xl"
              rounded="full"
            />
          </div>
        </div>

        {/* Welcome heading */}
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">
          Welcome to {teamName}
        </h1>

        {/* Short welcome subtitle */}
        <p className="mt-3 text-sm text-muted-foreground leading-relaxed max-w-sm mx-auto">
          Your AI-powered assistant is ready to help. Let&apos;s start with a
          quick setup to personalize your experience.
        </p>

        {/* Get Started button */}
        <Button
          size="lg"
          onClick={onGetStarted}
          className="mt-8 px-8"
        >
          <Sparkles className="mr-2 h-4 w-4" />
          Get Started
        </Button>
      </div>
    </div>
  );
}
